import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import moment from '@g4/moment-utc';

import { useTranslations } from '../../../../graphql/components/TranslationsProvider';
import { Box, Text, useMedia } from '../../../Boneyard';

import CALENDAR_ICON from '../../../../images/ic-calendar.svg';
import CAR_ICON from '../../../../images/ic-car.svg';
import INFO_ICON_BLUE from '../../../../images/icon-24-info-blue.svg';
import Markdown from '../../../Markdown';
import { getPrice } from '@g4/next-furniture/Price/Price';
import { CARS_MANDATORY_TAXES_INFO_DISCLAIMER } from '../../../../../automationHooks.json';

const propTypes = {
  vehicleItem: PropTypes.shape({
    id: PropTypes.string,
    vehicle: PropTypes.shape({
      description: PropTypes.string,
      category: PropTypes.string,
      type: PropTypes.string,
    }),
    vehiclePrice: PropTypes.shape({
      total: {
        amount: PropTypes.number,
      },
      taxesAndFeesDueAtRentalSite: {
        amount: PropTypes.number,
      },
    }),
    pickUpDate: PropTypes.string,
    dropOffDate: PropTypes.string,
  }),
  isTripSummary: PropTypes.bool,
  vehicleDaysCount: PropTypes.number,
  vehicleDaysCountLabel: PropTypes.string,
  vehiclePickUpLabel: PropTypes.string,
  vehicleDropOffLabel: PropTypes.string,
};

const BorderBox = styled(Box)`
  border-bottom: ${props =>
    props.isTripSummary && !props.isMobile
      ? `1px solid ${props.theme.colors.cart.footerBorder}`
      : 'none'};
`;

const CalendarIconContainer = styled.div`
  width: ${props => (props.isMobile ? '16px' : '24px')};
  height: ${props => (props.isMobile ? '16px' : '24px')};
  max-width: 100%;
  max-height: 100%;
  background-size: contain;
  background-repeat: no-repeat;
  margin-top: ${props => (props.isMobile ? '10px' : '4px')};
  margin-left: ${props => (props.isMobile ? '4px' : '0px')};
  background-image: url(${CALENDAR_ICON});
`;

const CarIconContainer = styled.div`
  width: ${props => (props.isMobile ? '22px' : '29px')};
  height: ${props => (props.isMobile ? '12px' : '16px')};
  max-width: 100%;
  max-height: 100%;
  background-size: contain;
  background-repeat: no-repeat;
  margin-top: ${props => (props.isMobile ? '14px' : '12px')};
  background-image: url(${CAR_ICON});
`;

const InfoIcon = styled.img`
  padding-right: 5px;
  padding-bottom: ${props => (props.isMobile ? '10px' : '0px')};
`;

const getVehicleDateTime = time => {
  const timeMoment = moment(time);
  const dateFormat = 'MMM DD, YYYY, hh:mm a';
  return timeMoment.format(dateFormat);
};

const getVehicleDate = (time, isMobile) => {
  const timeMoment = moment(time);
  const dateFormat = isMobile ? 'MMM DD, YYYY' : 'MMM DD, YYYY,';
  return timeMoment.format(dateFormat);
};

const getVehicleTime = time => {
  const timeMoment = moment(time);
  const dateFormat = 'hh:mm A';
  return timeMoment.format(dateFormat);
};

const CarInfo = ({
  vehicleItem,
  isTripSummary,
  vehicleDaysCount,
  vehicleDaysCountLabel,
}) => {
  const mq = useMedia();
  const isMobile = mq.lt('md');

  const translations = useTranslations();

  const { vehiclePickUpLabel, vehicleDropOffLabel } = isTripSummary
    ? translations.paymentPage
    : translations.cart;
  const { carFeesAndTaxesTextHTML } = isTripSummary && translations.paymentPage;

  const taxesAndFeesDueAtRentalSite =
    vehicleItem.vehiclePrice.taxesAndFeesDueAtRentalSite;

  return (
    <Box key={vehicleItem.id} pl={isTripSummary ? 1 : 3}>
      <BorderBox
        mb={isTripSummary && isMobile ? 0 : 2}
        pb={isTripSummary ? (isMobile ? 0 : 2) : 0}
        isTripSummary={isTripSummary}
        isMobile={isMobile}
      >
        <Text
          weight={600}
          size={isTripSummary ? (isMobile ? 14 : 18) : isMobile ? 14 : 16}
        >
          {vehicleItem.vehicle.description}
        </Text>
        {isTripSummary ? (
          ''
        ) : (
          <Box mt={1}>
            <Text weight={500} size={isMobile ? 13 : 14}>
              {vehicleItem.vehicle.category} {vehicleItem.vehicle.type} -{' '}
              {vehicleDaysCount} {vehicleDaysCountLabel}
            </Text>
          </Box>
        )}
      </BorderBox>

      <Box
        d="flex"
        direction={isTripSummary ? (isMobile ? 'column' : 'row') : 'column'}
        ml={isTripSummary ? (isMobile ? 0 : 3) : 0}
      >
        <Box d="flex">
          {isTripSummary ? <CalendarIconContainer isMobile={isMobile} /> : ''}
          <Box
            d="flex"
            alignItems={isTripSummary ? 'flex-start' : 'flex-end'}
            direction={isTripSummary ? 'column' : 'row'}
            mb={isTripSummary && isMobile ? 0 : 1}
            ml={isTripSummary ? (isMobile ? 2 : 4) : 0}
            mr={isTripSummary ? (isMobile ? 3 : 8) : 0}
          >
            <Box
              mt={isTripSummary ? 2 : 0}
              mb={isTripSummary ? (isMobile ? 1 : 2) : 0}
            >
              <Text
                weight={500}
                size={isTripSummary ? (isMobile ? 12 : 16) : 12}
              >
                {vehiclePickUpLabel}:
              </Text>
            </Box>
            {isTripSummary ? (
              <div>
                <Box mb={isMobile ? 1 : 0}>
                  <Text size={isMobile ? 13 : 16}>
                    {getVehicleDate(vehicleItem.pickUpDate, isMobile)}
                  </Text>
                </Box>
                <Text size={isMobile ? 13 : 16}>
                  {getVehicleTime(vehicleItem.pickUpDate)}
                </Text>
              </div>
            ) : (
              <Box ml={5}>
                <Text size={isMobile ? 13 : 14}>
                  {getVehicleDateTime(vehicleItem.pickUpDate)}
                </Text>
              </Box>
            )}
          </Box>
          {isTripSummary && isMobile ? (
            <Box d="flex">
              <CarIconContainer isMobile={isMobile} />
              <Box d="flex" direction="column" ml={1}>
                <Box d="flex" mt={2}>
                  <Text weight={500} size={isMobile ? 13 : 15}>
                    {vehicleItem.vehicle.category} {vehicleItem.vehicle.type}
                  </Text>
                </Box>
              </Box>
            </Box>
          ) : (
            ''
          )}
        </Box>

        <Box d="flex">
          {isTripSummary ? <CalendarIconContainer isMobile={isMobile} /> : ''}
          <Box
            d="flex"
            alignItems={isTripSummary ? 'flex-start' : 'flex-end'}
            direction={isTripSummary ? 'column' : 'row'}
            ml={isTripSummary ? (isMobile ? 2 : 4) : 0}
            mr={isTripSummary ? 12 : 0}
          >
            <Box
              mt={isTripSummary ? 2 : 0}
              mb={isTripSummary ? (isMobile ? 1 : 2) : 0}
            >
              <Text
                weight={500}
                size={isTripSummary ? (isMobile ? 12 : 16) : 12}
              >
                {vehicleDropOffLabel}:
              </Text>
            </Box>
            {isTripSummary ? (
              <div>
                <Box mb={isMobile ? 1 : 0}>
                  <Text size={isMobile ? 13 : 16}>
                    {getVehicleDate(vehicleItem.dropOffDate, isMobile)}
                  </Text>
                </Box>
                <Text size={isMobile ? 13 : 16}>
                  {getVehicleTime(vehicleItem.dropOffDate)}
                </Text>
              </div>
            ) : (
              <Box ml={4}>
                <Text size={isMobile ? 13 : 14}>
                  {getVehicleDateTime(vehicleItem.dropOffDate)}
                </Text>
              </Box>
            )}
          </Box>
        </Box>

        {isTripSummary && !isMobile ? (
          <Box d="flex" direction="row">
            <CarIconContainer isMobile={isMobile} />
            <Box d="flex" direction="column" ml={4}>
              <Box d="flex" mt={2}>
                <Text weight={500} size={isMobile ? 13 : 15}>
                  {vehicleItem.vehicle.category} {vehicleItem.vehicle.type}
                </Text>
              </Box>
            </Box>
          </Box>
        ) : (
          ''
        )}
      </Box>
      {isTripSummary &&
        taxesAndFeesDueAtRentalSite &&
        taxesAndFeesDueAtRentalSite.amount > 0 && (
          <Box
            pt={2}
            d="flex"
            direction="row"
            ml={isMobile ? 0 : 3}
            automationHook={CARS_MANDATORY_TAXES_INFO_DISCLAIMER.hook}
          >
            <InfoIcon
              src={INFO_ICON_BLUE}
              isMobile={isMobile}
              width={isMobile ? '19px' : '23px'}
            />
            <Text size={isMobile ? 12 : 16}>
              <Markdown
                source={carFeesAndTaxesTextHTML}
                data={{
                  amount: getPrice(taxesAndFeesDueAtRentalSite.amount).price,
                }}
              />
            </Text>
          </Box>
        )}
    </Box>
  );
};

CarInfo.propTypes = propTypes;

export default CarInfo;
